import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { publicEnv } from '@/lib/env';
import { MLP_TEAM_LEAGUE_SLUG } from '@/modules/homepage/RenderMLPLeaderboard/constants';
import { MLPLeaderboardOption } from '@/types/homepage.types';
import { WidgetMLPLeaderboard } from '@/types/mlpLeaderboard.types';
import { TEAM_LEAGUES_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';

export interface RenderMLPLeaderboardProps {
  rankings: WidgetMLPLeaderboard;
  metadata: MLPLeaderboardOption;
}

export const RenderMLPLeaderboard = ({ rankings, metadata }: RenderMLPLeaderboardProps) => {
  const [ranking, setRanking] = useState<{ value: string }>({
    value: rankings?.divisionsOptions[0]?.value || '',
  });

  const [stopChangingTabs, setStopChangingTabs] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data: rankingsData, isFetching } = useQuery({
    queryKey: ['rankings', ranking.value],
    queryFn: async () => {
      const response = await fetch(
        `/api/v2/mlpLeaderboardStandings?divisionType=${ranking.value}`,
        {
          method: 'GET',
        }
      );
      const result: { data: WidgetMLPLeaderboard; statusCode: number; error?: string } =
        await response.json();
      return result.data;
    },
    initialData: {
      widgetMLPLeaderboardData: rankings.widgetMLPLeaderboardData,
      divisionsOptions: rankings.divisionsOptions,
    },
  });

  const tabs = rankingsData?.divisionsOptions || [];
  const showTabs = tabs.length > 1;

  useEffect(() => {
    if (!ranking.value && tabs.length) {
      setRanking({
        value: tabs[0]?.value || '',
      });
    }
  }, [tabs, ranking.value]);

  useEffect(() => {
    if (metadata.changeTabs && !stopChangingTabs && showTabs) {
      // Start the interval
      intervalRef.current = setInterval(() => {
        const currentIndex = tabs.findIndex((option) => option.value === ranking.value);
        const nextIndex = (currentIndex + 1) % tabs.length;
        const nextTab = tabs[nextIndex];

        if (nextTab) {
          setRanking({
            value: nextTab.value,
          });
        }
      }, metadata.secondsInBetweenChanges * 1000);
    }

    // Cleanup function to clear the interval when the component unmounts or dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [ranking]);

  // Custom function to stop the interval
  const stopInterval = () => {
    setStopChangingTabs(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  return (
    <div className='rounded-xl bg-white shadow-sm'>
      <div className='flex items-center gap-2 border-b border-gray-200 p-4'>
        <img
          className='h-6 w-auto'
          alt='MLP logo'
          src={`${getCDNURL(`${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/outside/mlp-presented-by-doordash.png`, 47, 24)}`}
        />

        <h2 className='text-sm font-bold' tabIndex={0}>
          Leaderboard
        </h2>
      </div>
      <div className='flex justify-evenly border-b border-gray-200 px-2'>
        {tabs.map((option) => {
          const isCurrentSelected = ranking?.value === option.value;
          const tabItemClasses = clsx(
            'cursor-pointer border-b-2 border-transparent px-2 py-3 text-sm font-semibold uppercase text-gray-500 hover:text-experiment',
            {
              '!border-experiment !text-experiment': isCurrentSelected,
              'border-b-2 border-transparent': !isCurrentSelected,
            }
          );

          return (
            <div
              className={tabItemClasses}
              key={`mlp-leaderboard-${option.value}_${option.label}`}
              onClick={() => {
                stopInterval();
                setRanking({ value: option.value });
              }}
              tabIndex={0}
              role='button'
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  stopInterval();
                  setRanking({ value: option.value });
                }
              }}
            >
              {option.label}
            </div>
          );
        })}
      </div>

      <div className='flex h-[512px] flex-col pt-6'>
        {rankingsData &&
        rankingsData.widgetMLPLeaderboardData &&
        rankingsData.widgetMLPLeaderboardData.length > 0 ? (
          // Have data (from SSR initialData, previous fetch, or just-completed
          // refetch) — always render it. Don't replace valid data with a
          // spinner during background refetches; that's the bug that made the
          // widget appear to "load for ~1 min" on slow networks.
          <div className='relative flex-1'>
            <div className='flex items-center px-4'>
              <div className='w-11 shrink-0 text-left text-xs font-normal text-gray-500'>Rank</div>
              <div className='flex-1 pl-12 pr-7 text-xs font-normal text-gray-500'>Name</div>
              <div className='w-10 shrink-0 text-right text-xs font-normal text-gray-500'>
                Points
              </div>
            </div>
            <div className='flex flex-col divide-y divide-gray-200 overflow-auto'>
              {rankingsData.widgetMLPLeaderboardData.map((item) => (
                <div
                  key={`mlp-leaderboard-${item.teamLeagueUuid}-${item.ranking}`}
                  aria-label={`Team: ${item.teamLeagueName}, Rank: ${item.ranking}`}
                  className='hover:!bg-gray-200'
                >
                  <CustomLink
                    href={`${publicEnv.NEXT_PUBLIC_PTL_URL}/team-leagues/${MLP_TEAM_LEAGUE_SLUG}/team-details/${item.teamLeagueTeamUuid}/stats`}
                    aria-hidden='true'
                  >
                    <div className='flex items-center px-4 py-2'>
                      <div className='w-8 shrink-0 text-left text-xs font-bold'>
                        {item.ranking === '0' ? '-' : item.ranking}
                      </div>
                      <div className='mr-2 flex items-center justify-center'>
                        <img
                          src={getCDNURL(item.logo ?? TEAM_LEAGUES_PLACEHOLDER_IMAGE, 100, 100)}
                          className='w-16 h-6 object-contain rounded-sm'
                          alt={item.teamLeagueName}
                        />
                      </div>
                      <div className='mr-1 w-[166px] truncate text-sm text-gray-900'>
                        <b>{item.teamLeagueName}</b>
                      </div>
                      <div className='w-[38px] shrink-0 text-right text-xs font-bold'>
                        {Intl.NumberFormat('en-US').format(item.points)}
                      </div>
                    </div>
                  </CustomLink>
                </div>
              ))}
            </div>
          </div>
        ) : isFetching ? (
          <div className='flex h-full flex-1 items-center justify-center'>
            <FontAwesomeIcon size='lg' icon={faSpinnerThird} spin />
          </div>
        ) : (
          <div className='flex h-full flex-1 items-center justify-center text-sm text-gray-600'>
            New MLP season starting soon!
          </div>
        )}
        <CustomLink
          href={`${publicEnv.NEXT_PUBLIC_PTL_URL}/team-leagues/${MLP_TEAM_LEAGUE_SLUG}/standings`}
          className='flex items-center justify-center border-t border-gray-200 py-4 text-sm font-semibold text-experiment underline-offset-4 hover:underline'
        >
          View Full Leaderboard
        </CustomLink>
      </div>
    </div>
  );
};
